<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import DaIcon from '@/components/elements/daIcon/DaIcon.vue';
import { $api } from '@/services';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Loading from '@/components/common/Loading';

const store = useStore();
const route = useRoute();
const staffData = ref({
	staff: {
		full_name: '',
		id: null,
		profile_photo: {
			default: {
				color: '',
				initial: '',
			},
			state: '',
		},
	},
	private_information: {
		personal_information: {
			citizen_id_number: '',
			emergency_contact_1: '',
			emergency_contact_2: '',
			family_information: {
				children_info: [
					{
						dob: '',
						name: '',
					},
				],
				spouse_info: {
					dob: '',
					name: '',
					occupation: '',
					phone: '',
				},
			},
			health_status: '',
			marital_status: '',
			medical_conditions: '',
			permanent_address: '',
			personal_strengths: '',
			religion: '',
			temporary_address: '',
		},
		work_schedule_and_benefits: {
			allowances: '',
			current_base_salary: '',
			insurance_policy: true,
			overtime_policy: true,
			starting_salary: '',
			work_location: '',
			work_mode: '',
			working_hours: '',
		},
	},
	public_information: {
		attachments: {
			avatar_asset_key: '',
			avatar_asset_url: '',
			resume_asset_key: '',
			resume_url: '',
			resume_file_name: '',
			resume_type: '',
		},
		certificates_and_awards: {
			awards: [
				{
					award_description: '',
					award_file_asset_key: '',
					award_name: '',
					award_file_url: '',
					type: '',
					file_name: '',
				},
			],
			certificates: [
				{
					cert_description: '',
					cert_file_asset_key: '',
					cert_name: '',
					cert_file_url: '',
					type: '',
					file_name: '',
				},
			],
			degrees: [
				{
					degree_file_asset_key: '',
					degree_major: '',
					degree_name: '',
					institution_name: '',
					degree_file_url: '',
					type: '',
					file_name: '',
				},
			],
		},
		orientation_and_work_style: {
			collaboration_skills: '',
			intervention_philosophy: '',
			parent_commitment_principles: '',
			personal_slogan: '',
		},
		professional_qualifications: {
			academic_degree: '',
			academic_rank: '',
			development_activities: [],
			highest_education_level: '',
			key_achievements: [],
			language_skills: [],
			major: [],
			official_job_title: [],
			target_group_experience: [],
			tech_skills: [],
			years_of_experience: '',
		},
		staff_information: {
			name: '',
			date_of_birth: '',
			email: '',
			phone_number: '',
			gender: '',
		},
	},
});
const editMode = ref(false);
const alertShow = ref(true);
const formatDate = (dateString, reverse) => {
	const date = new Date(dateString);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	let formattedDate;
	if (reverse) formattedDate = `${year}-${month}-${day}`;
	else formattedDate = `${day}/${month}/${year}`;
	return formattedDate;
};
const isLoading = ref(false);
const type = ref(null);
const attachments = ref(null);
const changeAvatar = ref(false);

const editData = (id) => {
	editMode.value = true;
	nextTick(() => {
		const el = document.getElementById(id);
		if (el) {
			el.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}
	});
};
const addInput = (val) => {
	switch (val) {
		case 'major':
			staffData.value.public_information.professional_qualifications.major.push(
				''
			);
			break;
		case 'official_job_title':
			staffData.value.public_information.professional_qualifications.official_job_title.push(
				''
			);
			break;
		case 'target_group_experience':
			staffData.value.public_information.professional_qualifications.target_group_experience.push(
				''
			);
			break;
		case 'key_achievements':
			staffData.value.public_information.professional_qualifications.key_achievements.push(
				''
			);
			break;
		case 'language_skills':
			staffData.value.public_information.professional_qualifications.language_skills.push(
				''
			);
			break;
		case 'tech_skills':
			staffData.value.public_information.professional_qualifications.tech_skills.push(
				''
			);
			break;
		case 'development_activities':
			staffData.value.public_information.professional_qualifications.development_activities.push(
				''
			);
			break;
		case 'degrees':
			staffData.value.public_information.certificates_and_awards.degrees.push(
				{
					degree_name: '',
					institution_name: '',
					degree_major: '',
					degree_file_asset_key: '',
					degree_file_url: '',
					type: '',
					file_name: '',
				}
			);
			break;
		case 'certificates':
			staffData.value.public_information.certificates_and_awards.certificates.push(
				{
					cert_name: '',
					cert_description: '',
					cert_file_asset_key: '',
					cert_file_url: '',
					type: '',
					file_name: '',
				}
			);
			break;
		case 'awards':
			staffData.value.public_information.certificates_and_awards.awards.push(
				{
					award_name: '',
					award_description: '',
					award_file_asset_key: '',
					award_file_url: '',
					type: '',
					file_name: '',
				}
			);
			break;
		case 'children_info':
			staffData.value.private_information.personal_information.family_information.children_info.push(
				{
					name: '',
					dob: '',
				}
			);
			break;
	}
};

watch(editMode, (val) => {
	if (
		val &&
		staffData.value.public_information.certificates_and_awards.degrees
			.length === 0
	) {
		addInput('degrees');
	}
	if (
		val &&
		staffData.value.public_information.certificates_and_awards.awards
			.length === 0
	) {
		addInput('awards');
	}
	if (
		val &&
		staffData.value.public_information.certificates_and_awards.certificates
			.length === 0
	) {
		addInput('certificates');
	}

	if (
		val &&
		staffData.value.private_information.personal_information
			.family_information.children_info.length === 0
	) {
		addInput('children_info');
	}
	if (
		val &&
		staffData.value.public_information.professional_qualifications.major
			.length === 0
	) {
		addInput('major');
	}

	if (
		val &&
		staffData.value.public_information.professional_qualifications
			.official_job_title.length === 0
	) {
		addInput('official_job_title');
	}
	if (
		val &&
		staffData.value.public_information.professional_qualifications
			.target_group_experience.length === 0
	) {
		addInput('target_group_experience');
	}
	if (
		val &&
		staffData.value.public_information.professional_qualifications
			.key_achievements.length === 0
	) {
		addInput('key_achievements');
	}
	if (
		val &&
		staffData.value.public_information.professional_qualifications
			.language_skills.length === 0
	) {
		addInput('language_skills');
	}
	if (
		val &&
		staffData.value.public_information.professional_qualifications
			.tech_skills.length === 0
	) {
		addInput('tech_skills');
	}

	if (
		val &&
		staffData.value.public_information.professional_qualifications
			.development_activities.length === 0
	) {
		addInput('development_activities');
	}
});

const deleteInput = (index, type) => {
	switch (type) {
		case 'degrees':
			staffData.value.public_information.certificates_and_awards.degrees.splice(
				index,
				1
			);
			break;
		case 'certificates':
			staffData.value.public_information.certificates_and_awards.certificates.splice(
				index,
				1
			);
			break;
		case 'awards':
			staffData.value.public_information.certificates_and_awards.awards.splice(
				index,
				1
			);
			break;
		case 'children_info':
			staffData.value.private_information.personal_information.family_information.children_info.splice(
				index,
				1
			);
			break;
		case 'major':
			staffData.value.public_information.professional_qualifications.major.splice(
				index,
				1
			);
			break;
		case 'official_job_title':
			staffData.value.public_information.professional_qualifications.official_job_title.splice(
				index,
				1
			);
			break;
		case 'target_group_experience':
			staffData.value.public_information.professional_qualifications.target_group_experience.splice(
				index,
				1
			);
			break;
		case 'key_achievements':
			staffData.value.public_information.professional_qualifications.key_achievements.splice(
				index,
				1
			);
			break;
		case 'language_skills':
			staffData.value.public_information.professional_qualifications.language_skills.splice(
				index,
				1
			);
			break;
		case 'tech_skills':
			staffData.value.public_information.professional_qualifications.tech_skills.splice(
				index,
				1
			);
			break;
		case 'development_activities':
			staffData.value.public_information.professional_qualifications.development_activities.splice(
				index,
				1
			);
			break;
	}
};
onMounted(() => {
	fetchData();
});
const indexImage = ref(null);
const openModelUpload = (data, index) => {
	type.value = data;
	indexImage.value = index;
	const target = document.getElementById('upload-images-file');
	target ? target.click() : '';
};
const closeUpload = () => {
	const target = document.getElementById('upload-images-file');
	target ? (target.value = null) : '';
};

const isImage = (icon) => {
	const ext = ['.jpg', '.jpeg', '.png', '.svg'];
	return ext.some((el) => icon.toLowerCase().endsWith(el));
};

const uploadFiles = (event) => {
	let fileUploads = Array.from(event?.target?.files || []);
	if (!fileUploads || !fileUploads.length) {
		return;
	}

	fileUploads.forEach((file) => {
		switch (type.value) {
			case 'avatar':
				if (!isImage(file.name)) return;
				changeAvatar.value = true;
				staffData.value.public_information.attachments.avatar_asset_url =
					getUrl(file);
				staffData.value.public_information.attachments.avatar_url =
					getUrl(file);
				staffData.value.public_information.attachments.avatar_file =
					file;
				break;
			case 'resume':
				staffData.value.public_information.attachments.resume_file_name =
					file.name;
				staffData.value.public_information.attachments.resume_type =
					file.type;
				staffData.value.public_information.attachments.resume_url =
					getUrl(file);
				staffData.value.public_information.attachments.resume_file =
					file;
				break;
			case 'certificates':
				staffData.value.public_information.certificates_and_awards.certificates[
					indexImage.value
				].file_name = file.name;
				staffData.value.public_information.certificates_and_awards.certificates[
					indexImage.value
				].type = file.type;
				staffData.value.public_information.certificates_and_awards.certificates[
					indexImage.value
				].cert_file_url = getUrl(file);
				staffData.value.public_information.certificates_and_awards.certificates[
					indexImage.value
				].cert_file = file;
				break;
			case 'degrees':
				staffData.value.public_information.certificates_and_awards.degrees[
					indexImage.value
				].file_name = file.name;
				staffData.value.public_information.certificates_and_awards.degrees[
					indexImage.value
				].type = file.type;
				staffData.value.public_information.certificates_and_awards.degrees[
					indexImage.value
				].degree_file_url = getUrl(file);
				staffData.value.public_information.certificates_and_awards.degrees[
					indexImage.value
				].degree_file = file;
				break;
			case 'awards':
				staffData.value.public_information.certificates_and_awards.awards[
					indexImage.value
				].file_name = file.name;
				staffData.value.public_information.certificates_and_awards.awards[
					indexImage.value
				].type = file.type;
				staffData.value.public_information.certificates_and_awards.awards[
					indexImage.value
				].award_file_url = getUrl(file);
				staffData.value.public_information.certificates_and_awards.awards[
					indexImage.value
				].award_file = file;
				break;
		}
	});
	closeUpload();
};
const removeImage = (index, type) => {
	switch (type) {
		case 'degrees':
			staffData.value.public_information.certificates_and_awards.degrees[
				index
			].degree_file_url = '';
			staffData.value.public_information.certificates_and_awards.degrees[
				index
			].degree_file_asset_key = '';
			staffData.value.public_information.certificates_and_awards.degrees[
				index
			].type = '';
			staffData.value.public_information.certificates_and_awards.degrees[
				index
			].file_name = '';
			staffData.value.public_information.certificates_and_awards.degrees[
				index
			].degree_file = null;
			break;
		case 'certificates':
			staffData.value.public_information.certificates_and_awards.certificates[
				index
			].cert_file_url = '';
			staffData.value.public_information.certificates_and_awards.certificates[
				index
			].cert_file_asset_key = '';
			staffData.value.public_information.certificates_and_awards.certificates[
				index
			].type = '';
			staffData.value.public_information.certificates_and_awards.certificates[
				index
			].file_name = '';
			staffData.value.public_information.certificates_and_awards.certificates[
				index
			].cert_file = null;
			break;
		case 'awards':
			staffData.value.public_information.certificates_and_awards.awards[
				index
			].award_file_url = '';
			staffData.value.public_information.certificates_and_awards.awards[
				index
			].award_file_asset_key = '';
			staffData.value.public_information.certificates_and_awards.awards[
				index
			].type = '';
			staffData.value.public_information.certificates_and_awards.awards[
				index
			].file_name = '';
			staffData.value.public_information.certificates_and_awards.awards[
				index
			].award_file = null;
			break;
		case 'resume':
			staffData.value.public_information.attachments.resume_url = '';
			staffData.value.public_information.attachments.resume_asset_key =
				'';
			staffData.value.public_information.attachments.resume_type = '';
			staffData.value.public_information.attachments.resume_file_name =
				'';
			staffData.value.public_information.attachments.resume_file = null;
			break;
	}
};
const getUrl = (file) => {
	if (!file || file?.asset_url || file?.url || file?.degree_file_url) {
		return file?.asset_url || file?.url || '';
	}
	return URL.createObjectURL(file);
};
const getSignatures = async (listImage) => {
	if (listImage.length === 0) {
		return;
	}
	const fileList = listImage;
	const {
		data: { data },
	} = await $api.file.getSignatures({
		mime_types: fileList.map((f) => f.type),
		prefix: 'staff_profile',
		// bucket: 'lucastaging',
	});
	if (data && data.length) {
		for (let i = 0; i < data.length; i++) {
			if (
				fileList[i].cert_file_asset_key ||
				fileList[i].degree_file_asset_key ||
				fileList[i].award_file_asset_key
			)
				continue;
			let formData = new FormData();
			formData.append('AWSAccessKeyId', data[0]['AWSAccessKeyId'] || '');
			formData.append('key', data[i].key || '');
			formData.append('acl', data[i].acl || '');
			formData.append('Content-Type', data[i]['Content-Type'] || '');
			formData.append('x-amz-signature', data[i].signature || '');
			formData.append('Signature', data[i].signature || '');
			formData.append('Policy', data[i].policy || '');
			formData.append(
				'success_action_status',
				data[i].success_action_status || ''
			);
			// formData.append('bucket', 'lucastaging');

			// Get the actual File object
			let actualFile = null;
			if (fileList[i].cert_file) {
				actualFile = fileList[i].cert_file;
			} else if (fileList[i].degree_file) {
				actualFile = fileList[i].degree_file;
			} else if (fileList[i].award_file) {
				actualFile = fileList[i].award_file;
			} else if (type.value === 'avatar') {
				actualFile =
					staffData.value.public_information.attachments.avatar_file;
			} else if (type.value === 'resume') {
				actualFile =
					staffData.value.public_information.attachments.resume_file;
			}

			if (!actualFile) {
				console.error(
					'No file object found for upload',
					fileList[i],
					type.value
				);
				continue;
			}

			formData.append('file', actualFile);
			await uploadToS3(data[i].action, formData, fileList[i].value, i);
		}
	}
};

const uploadToS3 = async (url, payload, file, index) => {
	try {
		const { data } = await $api.file.uploadToS3(url, payload);
		const keyData = data.toString().match('<Key>(.*)</Key>');
		switch (type.value) {
			case 'avatar':
				staffData.value.public_information.attachments.avatar_asset_key =
					keyData && keyData.length ? keyData[1] : '';
				break;
			case 'resume':
				staffData.value.public_information.attachments.resume_asset_key =
					keyData && keyData.length ? keyData[1] : '';
				break;
			case 'certificates':
				staffData.value.public_information.certificates_and_awards.certificates[
					index
				].cert_file_asset_key =
					(keyData && keyData.length ? keyData[1] : '') || '';
				break;
			case 'degrees':
				staffData.value.public_information.certificates_and_awards.degrees[
					index
				].degree_file_asset_key =
					(keyData && keyData.length ? keyData[1] : '') || '';
				break;
			case 'awards':
				staffData.value.public_information.certificates_and_awards.awards[
					index
				].award_file_asset_key =
					(keyData && keyData.length ? keyData[1] : '') || '';
				break;
		}
	} catch (e) {
		store.dispatch('setAlertMessage', {
			message:
				e?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};
const fetchData = async () => {
	try {
		isLoading.value = true;
		const res = await $api.staff.staffProfile(route.params.id);
		staffData.value = res.data.data;
		attachments.value =
			staffData.value.public_information.attachments || null;
		if (
			staffData.value.public_information.certificates_and_awards.degrees
				.length === 0
		) {
			addInput('degrees');
		}
		if (
			staffData.value.public_information.certificates_and_awards.awards
				.length === 0
		) {
			addInput('awards');
		}
		if (
			staffData.value.public_information.certificates_and_awards
				.certificates.length === 0
		) {
			addInput('certificates');
		}
		isLoading.value = false;
	} catch (err) {
		isLoading.value = false;
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};
const updateProfile = async () => {
	try {
		if (changeAvatar.value) {
			type.value = 'avatar';
			await getSignatures([
				{
					type: 'image/png',
				},
			]);
		}
		if (
			staffData.value.public_information.attachments.resume_file_name &&
			!staffData.value.public_information.attachments.resume_asset_key
		) {
			type.value = 'resume';
			await getSignatures([
				{
					name: staffData.value.public_information.attachments
						.resume_file_name,
					type: staffData.value.public_information.attachments
						.resume_type,
				},
			]);
		}
		if (staffData.value.public_information.staff_information.date_of_birth)
			staffData.value.public_information.staff_information.date_of_birth =
				formatDate(
					staffData.value.public_information.staff_information
						.date_of_birth,
					true
				);
		if (
			staffData.value.public_information.certificates_and_awards.degrees
				.length > 0
		) {
			type.value = 'degrees';
			await getSignatures(
				staffData.value.public_information.certificates_and_awards.degrees.filter(
					(i) => i.file_name || i.degree_file_asset_key
				)
			);
		}
		if (
			staffData.value.public_information.certificates_and_awards
				.certificates.length > 0
		) {
			type.value = 'certificates';
			await getSignatures(
				staffData.value.public_information.certificates_and_awards.certificates.filter(
					(i) => i.file_name || i.cert_file_asset_key
				)
			);
		}
		if (
			staffData.value.public_information.certificates_and_awards.awards
				.length > 0
		) {
			type.value = 'awards';
			await getSignatures(
				staffData.value.public_information.certificates_and_awards.awards.filter(
					(i) => i.file_name || i.award_file_asset_key
				)
			);
		}
		const res = await $api.staff.updateStaffProfile(route.params.id, {
			staff_profile: staffData.value,
		});
		if (res) {
			editMode.value = false;
			changeAvatar.value = false;
			await store.dispatch('setAlertMessage', {
				message: 'Cập nhật thông tin thành công',
				type: 'success',
			});
		}
	} catch (err) {
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};
</script>

<template>
	<input
		type="file"
		hidden
		id="upload-images-file"
		@change="uploadFiles"
		multiple
		accept="application/pdf, image/png, image/jpeg, image/jpg"
	/>
	<div class="flex justify-end mb-4" v-if="!editMode">
		<div
			class="luca-button"
			@click="$router.push(`/record/employee/${route.params.id}/preview`)"
		>
			Xem trước hồ sơ gửi Phụ huynh
		</div>
	</div>
	<div
		v-if="alertShow"
		class="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded-md mb-4"
	>
		<div class="relative">
			<div class="flex items-center gap-4 pr-8">
				<div>
					<img
						src="@/assets/images/logo/logo.svg"
						alt="luca-logo"
						height="24"
						width="35"
					/>
				</div>
				<div class="pt-1 text-neutral-600 text-sm">
					Các thông tin nội bộ sẽ KHÔNG được hiển thị cho Phụ Huynh
				</div>
			</div>
			<div
				class="top-1 right-2 absolute cursor-pointer"
				@click="alertShow = false"
			>
				<svg
					width="12"
					height="12"
					viewBox="0 0 12 12"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L6 4.58579L10.2929 0.292893C10.6834 -0.0976311 11.3166 -0.0976311 11.7071 0.292893C12.0976 0.683417 12.0976 1.31658 11.7071 1.70711L7.41421 6L11.7071 10.2929C12.0976 10.6834 12.0976 11.3166 11.7071 11.7071C11.3166 12.0976 10.6834 12.0976 10.2929 11.7071L6 7.41421L1.70711 11.7071C1.31658 12.0976 0.683417 12.0976 0.292893 11.7071C-0.0976311 11.3166 -0.0976311 10.6834 0.292893 10.2929L4.58579 6L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z"
						fill="#E5A42B"
					/>
				</svg>
			</div>
		</div>
	</div>
	<div v-if="isLoading">
		<Loading />
	</div>
	<div v-else class="flex gap-8">
		<div class="flex flex-col gap-8 block-width">
			<div class="bg-white rounded-2xl shadow-sm w-full" id="1">
				<div class="flex items-center justify-between py-4 px-6 gap-2">
					<div class="font-semibold text-neutral-600">
						Phần 1: Thông tin cá nhân
					</div>
					<DaIcon
						v-if="!editMode"
						name="das--pen"
						class="h-5 w-5 cursor-pointer text-blue-600 hover:text-blue-900 active:text-blue-500"
						@click="editData('1')"
					/>
					<button
						v-else
						type="button"
						class="button-p text-center w-max"
						@click="updateProfile"
					>
						Lưu thay đổi
					</button>
				</div>
				<div class="py-4 px-6">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Họ và tên
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Nhập họ và tên"
								v-model="
									staffData.public_information
										.staff_information.name
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.public_information.staff_information
									.name || '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4 px-6">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Ngày sinh
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<VueDatepicker
								class="input-date w-full"
								:placeholder="`Ngày sinh của ${$filters.terminology(
									'teacher_short_name'
								)}`"
								autoApply
								:enableTimePicker="false"
								uid="filter-by-date"
								format="dd/MM/yyyy"
								minutesIncrement="30"
								v-model="
									staffData.public_information
										.staff_information.date_of_birth
								"
								:maxDate="new Date()"
							>
								<template #input-icon>
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M5 0.625C5 0.279822 4.72018 0 4.375 0C4.02982 0 3.75 0.279822 3.75 0.625V1.25H2.5C1.11929 1.25 0 2.36929 0 3.75V5H20V3.75C20 2.36929 18.8807 1.25 17.5 1.25H16.25V0.625C16.25 0.279822 15.9702 0 15.625 0C15.2798 0 15 0.279822 15 0.625V1.25H5V0.625ZM20 17.5V6.25H0V17.5C0 18.8807 1.11929 20 2.5 20H17.5C18.8807 20 20 18.8807 20 17.5ZM11.875 8.75C11.5298 8.75 11.25 9.02982 11.25 9.375V10.625C11.25 10.9702 11.5298 11.25 11.875 11.25H13.125C13.4702 11.25 13.75 10.9702 13.75 10.625V9.375C13.75 9.02982 13.4702 8.75 13.125 8.75H11.875ZM15.625 8.75C15.2798 8.75 15 9.02982 15 9.375V10.625C15 10.9702 15.2798 11.25 15.625 11.25H16.875C17.2202 11.25 17.5 10.9702 17.5 10.625V9.375C17.5 9.02982 17.2202 8.75 16.875 8.75H15.625ZM2.5 13.125C2.5 12.7798 2.77982 12.5 3.125 12.5H4.375C4.72018 12.5 5 12.7798 5 13.125V14.375C5 14.7202 4.72018 15 4.375 15H3.125C2.77982 15 2.5 14.7202 2.5 14.375V13.125ZM6.875 12.5C6.52982 12.5 6.25 12.7798 6.25 13.125V14.375C6.25 14.7202 6.52982 15 6.875 15H8.125C8.47018 15 8.75 14.7202 8.75 14.375V13.125C8.75 12.7798 8.47018 12.5 8.125 12.5H6.875Z"
											fill="#94A3B8"
										/>
									</svg>
								</template>
							</VueDatepicker>
						</div>
						<div v-else>
							{{
								staffData.public_information?.staff_information
									?.date_of_birth
									? formatDate(
											staffData.public_information
												.staff_information.date_of_birth
									  )
									: '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4 px-6 w-full">
					<div class="flex items-center justify-between w-full">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs w-1/2"
						>
							Giới tính
						</div>
						<div
							v-if="editMode"
							class="w-full flex items-center justify-end gap-10"
						>
							<div class="flex items-center gap-2">
								<input
									value="male"
									type="radio"
									class="h-4 w-4 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600 cursor-pointer"
									v-model="
										staffData.public_information
											.staff_information.gender
									"
								/>
								<div class="text-neutral-7600">Nam</div>
							</div>
							<div class="flex items-center gap-2">
								<input
									value="female"
									type="radio"
									class="h-4 w-4 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600 cursor-pointer"
									v-model="
										staffData.public_information
											.staff_information.gender
									"
								/>
								<div class="mr-6 text-neutral-7600">Nữ</div>
							</div>
						</div>
					</div>
					<div class="mt-1 input-group" v-if="!editMode">
						<div>
							{{
								staffData.public_information.staff_information
									.gender === 'male'
									? 'Nam'
									: staffData.public_information
											.staff_information.gender ===
									  'female'
									? 'Nữ'
									: '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4 px-6">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						SĐT cá nhân
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Nhập số điện thoại"
								v-model="
									staffData.public_information
										.staff_information.phone_number
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.public_information.staff_information
									.phone_number || '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4 px-6">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Email cá nhân
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Nhập email"
								v-model="
									staffData.public_information
										.staff_information.email
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.public_information.staff_information
									.email || '-'
							}}
						</div>
					</div>
				</div>
				<div class="private-bg px-6">
					<div
						class="py-4 text-neutral-500 font-light text-sm italic"
					>
						Thông tin nội bộ
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Tôn giáo
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Ví dụ: Không / Phật giáo / Thiên Chúa giáo..."
									v-model="
										staffData.private_information
											.personal_information.religion
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information.religion || '-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Số CCCD
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Nhập số căn cước công dân"
									v-model="
										staffData.private_information
											.personal_information
											.citizen_id_number
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information
										.citizen_id_number || '-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Địa chỉ thường trú
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Nhập địa chỉ thường trú"
									v-model="
										staffData.private_information
											.personal_information
											.permanent_address
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information
										.permanent_address || '-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Địa chỉ tạm trú
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Nhập địa chỉ tạm trú"
									v-model="
										staffData.private_information
											.personal_information
											.temporary_address
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information
										.temporary_address || '-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Tình trạng sức khỏe
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Ví dụ: Sức khỏe tốt / Có vấn đề về thị lực nhẹ"
									v-model="
										staffData.private_information
											.personal_information.health_status
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information.health_status ||
									'-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Bệnh lý cần lưu ý
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Ví dụ: Dị ứng thuốc kháng sinh / Hen suyễn"
									v-model="
										staffData.private_information
											.personal_information
											.medical_conditions
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information
										.medical_conditions || '-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Tình trạng hôn nhân
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Ví dụ: Độc thân / Đã kết hôn"
									v-model="
										staffData.private_information
											.personal_information.marital_status
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information.marital_status ||
									'-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div class="text-neutral-800">Thông tin vợ/chồng</div>
						<div class="py-4 pl-4">
							<div
								class="uppercase text-neutral-400 font-semibold text-xs"
							>
								Họ và tên
							</div>
							<div class="mt-1 input-group">
								<div v-if="editMode" class="w-full">
									<input
										type="text"
										placeholder="Nhập họ và tên đầy đủ của vợ/chồng"
										v-model="
											staffData.private_information
												.personal_information
												.family_information.spouse_info
												.name
										"
									/>
								</div>
								<div v-else>
									{{
										staffData.private_information
											.personal_information
											.family_information.spouse_info
											.name || '-'
									}}
								</div>
							</div>
						</div>
						<div class="py-4 pl-4">
							<div
								class="uppercase text-neutral-400 font-semibold text-xs"
							>
								Năm sinh
							</div>
							<div class="mt-1 input-group">
								<div v-if="editMode" class="w-full">
									<input
										type="number"
										placeholder="Nhập năm sinh của vợ/chồng (VD: 1995)"
										v-model="
											staffData.private_information
												.personal_information
												.family_information.spouse_info
												.dob
										"
									/>
								</div>
								<div v-else>
									{{
										staffData.private_information
											.personal_information
											.family_information.spouse_info.dob
											? formatDate(
													staffData
														.private_information
														.personal_information
														.family_information
														.spouse_info.dob
											  )
											: '-'
									}}
								</div>
							</div>
						</div>
						<div class="py-4 pl-4">
							<div
								class="uppercase text-neutral-400 font-semibold text-xs"
							>
								Nghề nghiệp
							</div>
							<div class="mt-1 input-group">
								<div v-if="editMode" class="w-full">
									<input
										type="text"
										placeholder="Nhập nghề nghiệp hiện tại của vợ/chồng"
										v-model="
											staffData.private_information
												.personal_information
												.family_information.spouse_info
												.occupation
										"
									/>
								</div>
								<div v-else>
									{{
										staffData.private_information
											.personal_information
											.family_information.spouse_info
											.occupation || '-'
									}}
								</div>
							</div>
						</div>
						<div class="py-4 pl-4">
							<div
								class="uppercase text-neutral-400 font-semibold text-xs"
							>
								Số điện thoại
							</div>
							<div class="mt-1 input-group">
								<div v-if="editMode" class="w-full">
									<input
										type="text"
										placeholder="Nhập số điện thoại liên hệ của vợ/chồng"
										v-model="
											staffData.private_information
												.personal_information
												.family_information.spouse_info
												.phone
										"
									/>
								</div>
								<div v-else>
									{{
										staffData.private_information
											.personal_information
											.family_information.spouse_info
											.phone || '-'
									}}
								</div>
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							v-if="
								staffData.private_information
									.personal_information.family_information
									.children_info.length > 0
							"
						>
							<div
								v-for="(son, index) in staffData
									.private_information.personal_information
									.family_information.children_info"
								:key="son"
							>
								<div class="flex items-center justify-between">
									<div class="text-neutral-800">
										Thông tin con {{ index + 1 }}
									</div>
									<div
										v-if="
											editMode &&
											staffData.private_information
												.personal_information
												.family_information
												.children_info.length > 1
										"
										class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
										@click="
											deleteInput(index, 'children_info')
										"
									>
										<img
											src="@/assets/images/icons/trash-fill-blue.png"
											class="w-6 h-6 cursor-pointer"
											alt=""
										/>
									</div>
								</div>

								<div class="py-4 pl-4">
									<div
										class="uppercase text-neutral-400 font-semibold text-xs"
									>
										Họ và tên
									</div>
									<div class="mt-1 input-group">
										<div v-if="editMode" class="w-full">
											<input
												type="text"
												placeholder="Nhập họ và tên đầy đủ của con"
												v-model="
													staffData
														.private_information
														.personal_information
														.family_information
														.children_info[index]
														.name
												"
											/>
										</div>
										<div v-else>
											{{ son.name || '-' }}
										</div>
									</div>
								</div>
								<div class="py-4 pl-4">
									<div
										class="uppercase text-neutral-400 font-semibold text-xs"
									>
										Năm sinh
									</div>
									<div class="mt-1 input-group">
										<div v-if="editMode" class="w-full">
											<input
												type="text"
												placeholder="Nhập năm sinh của con (VD: 2019)"
												v-model="
													staffData
														.private_information
														.personal_information
														.family_information
														.children_info[index]
														.dob
												"
											/>
										</div>
										<div v-else>
											{{
												son.dob
													? formatDate(son.dob)
													: '-'
											}}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-else>
							<div class="text-neutral-800">Thông tin con</div>
							<div class="p-4">-</div>
						</div>
						<div
							v-if="editMode"
							class="flex items-center justify-end w-full mt-2 cursor-pointer pr-4"
							@click="addInput('children_info')"
						>
							<img
								src="@/assets/images/icons/yellow-plus.png"
								alt=""
							/>
							<div
								class="ml-1 font-semibold text-yellow-700 text-sm'"
							>
								Thêm thông tin con
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Số điện thoại khẩn cấp 1 (bố/mẹ, vợ/chồng)
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Nhập số điện thoại khẩn cấp 1"
									v-model="
										staffData.private_information
											.personal_information
											.emergency_contact_1
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information
										.emergency_contact_1 || '-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Số điện thoại khẩn cấp 2 (bố/mẹ, vợ/chồng)
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Nhập số điện thoại khẩn cấp 2"
									v-model="
										staffData.private_information
											.personal_information
											.emergency_contact_2
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information
										.emergency_contact_2 || '-'
								}}
							</div>
						</div>
					</div>
					<div class="py-4">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs"
						>
							Thế mạnh, sở trường (nếu có)
						</div>
						<div class="mt-1 input-group">
							<div v-if="editMode" class="w-full">
								<input
									type="text"
									placeholder="Nhập thế mạnh hoặc sở trường của bạn"
									v-model="
										staffData.private_information
											.personal_information
											.personal_strengths
									"
								/>
							</div>
							<div v-else>
								{{
									staffData.private_information
										.personal_information
										.personal_strengths || '-'
								}}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="private-bg rounded-2xl shadow-sm w-full px-6" id="4">
				<div class="flex items-center justify-between py-4 gap-2">
					<div>
						<div class="font-semibold text-neutral-600">
							Phần 4: Lịch làm việc và chế độ
						</div>
						<div class="text-neutral-500 font-light text-sm italic">
							Thông tin nội bộ
						</div>
					</div>
					<DaIcon
						v-if="!editMode"
						name="das--pen"
						class="h-5 w-5 cursor-pointer text-blue-600 hover:text-blue-900 active:text-blue-500"
						@click="editData('4')"
					/>
					<button
						v-else
						type="button"
						class="button-p text-center w-max"
						@click="updateProfile"
					>
						Lưu thay đổi
					</button>
				</div>

				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Thời gian làm việc
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Ví dụ: Thứ 2 - Thứ 6 / Thứ 2 - Chủ nhật"
								v-model="
									staffData.private_information
										.work_schedule_and_benefits
										.working_hours
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.private_information
									.work_schedule_and_benefits.working_hours ||
								'-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Hình thức làm việc
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Ví dụ: Toàn thời gian /  bán thời gian / cộng tác,..."
								v-model="
									staffData.private_information
										.work_schedule_and_benefits.work_mode
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.private_information
									.work_schedule_and_benefits.work_mode || '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Địa điểm làm việc
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Ví dụ: Tại trung tâm / có thể di chuyển"
								v-model="
									staffData.private_information
										.work_schedule_and_benefits
										.work_location
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.private_information
									.work_schedule_and_benefits.work_location ||
								'-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4 w-full">
					<div class="flex items-center justify-between w-full">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs w-1/2"
						>
							Chế độ bảo hiểm
						</div>
						<div
							v-if="editMode"
							class="w-full flex items-center justify-end gap-10"
						>
							<div class="flex items-center gap-2">
								<input
									value="true"
									type="radio"
									class="h-4 w-4 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600 cursor-pointer"
									v-model="
										staffData.private_information
											.work_schedule_and_benefits
											.insurance_policy
									"
								/>
								<div class="text-neutral-7600">Có</div>
							</div>
							<div class="flex items-center gap-2">
								<input
									value="false"
									type="radio"
									class="h-4 w-4 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600 cursor-pointer"
									v-model="
										staffData.private_information
											.work_schedule_and_benefits
											.insurance_policy
									"
								/>
								<div class="mr-6 text-neutral-7600">Không</div>
							</div>
						</div>
					</div>
					<div class="mt-1 input-group" v-if="!editMode">
						<div>
							{{
								staffData.private_information
									.work_schedule_and_benefits.insurance_policy
									? 'Có'
									: 'Không'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Lương khởi điểm
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Ví dụ: 8.000.000 VNĐ / tháng"
								v-model="
									staffData.private_information
										.work_schedule_and_benefits
										.starting_salary
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.private_information
									.work_schedule_and_benefits
									.starting_salary || '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Lương cơ bản hiện tại
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Ví dụ: 12.000.000 VNĐ / tháng"
								v-model="
									staffData.private_information
										.work_schedule_and_benefits
										.current_base_salary
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.private_information
									.work_schedule_and_benefits
									.current_base_salary || '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Chế độ phụ cấp (nếu có)
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<input
								type="text"
								placeholder="Ví dụ: Phụ cấp ăn trưa, đi lại, chuyên cần"
								v-model="
									staffData.private_information
										.work_schedule_and_benefits.allowances
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.private_information
									.work_schedule_and_benefits.allowances ||
								'-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4 w-full">
					<div class="flex items-center justify-between w-full">
						<div
							class="uppercase text-neutral-400 font-semibold text-xs w-1/2"
						>
							Làm ngoài giờ
						</div>
						<div
							v-if="editMode"
							class="w-full flex items-center justify-end gap-10"
						>
							<div class="flex items-center gap-2">
								<input
									value="true"
									type="radio"
									class="h-4 w-4 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600 cursor-pointer"
									v-model="
										staffData.private_information
											.work_schedule_and_benefits
											.overtime_policy
									"
								/>
								<div class="text-neutral-7600">Có</div>
							</div>
							<div class="flex items-center gap-2">
								<input
									value="false"
									type="radio"
									class="h-4 w-4 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600 cursor-pointer"
									v-model="
										staffData.private_information
											.work_schedule_and_benefits
											.overtime_policy
									"
								/>
								<div class="mr-6 text-neutral-7600">Không</div>
							</div>
						</div>
					</div>
					<div class="mt-1 input-group" v-if="!editMode">
						<div>
							{{
								staffData.private_information
									.work_schedule_and_benefits.overtime_policy
									? 'Có'
									: 'Không'
							}}
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="flex flex-col gap-8 block-width">
			<div class="bg-white rounded-2xl shadow-sm w-full px-6" id="2">
				<div class="flex items-center justify-between py-4 gap-2">
					<div class="font-semibold text-neutral-600">
						Phần 2: Trình độ chuyên môn
					</div>
					<DaIcon
						v-if="!editMode"
						name="das--pen"
						class="h-5 w-5 cursor-pointer text-blue-600 hover:text-blue-900 active:text-blue-500"
						@click="editData('2')"
					/>
					<button
						v-else
						type="button"
						class="button-p text-center w-max"
						@click="updateProfile"
					>
						Lưu thay đổi
					</button>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Chuyên ngành đào tạo
					</div>
					<div
						v-if="
							staffData.public_information
								.professional_qualifications.major.length > 0
						"
					>
						<div
							v-for="(m, i) in staffData.public_information
								.professional_qualifications.major"
							:key="i"
						>
							<div class="mt-1 input-group">
								<div v-if="editMode">
									<div class="flex items-center gap-2">
										<input
											type="text"
											placeholder="Ví dụ: Tâm lý học lâm sàng / Giáo dục đặc biệt"
											v-model="
												staffData.public_information
													.professional_qualifications
													.major[i]
											"
										/>
										<div
											v-if="
												staffData.public_information
													.professional_qualifications
													.major[i]
											"
											class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
											@click="deleteInput(i, 'major')"
										>
											<img
												src="@/assets/images/icons/trash-fill-blue.png"
												class="w-6 h-6 cursor-pointer"
												alt=""
											/>
										</div>
									</div>
								</div>
								<div v-else>{{ m || '-' }}</div>
							</div>
						</div>
					</div>
					<div v-else>-</div>
					<div
						v-if="editMode"
						class="flex items-center justify-end w-full mt-2 cursor-pointer"
						@click="addInput('major')"
					>
						<img
							src="@/assets/images/icons/yellow-plus.png"
							alt=""
						/>
						<div
							class="ml-1 font-semibold text-yellow-700 text-sm'"
						>
							Thêm thông tin
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Trình độ học vấn cao nhất
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode">
							<input
								type="text"
								placeholder="Ví dụ: Trung cấp / Cao đẳng / Đại học / Sau đại học,..."
								v-model="
									staffData.public_information
										.professional_qualifications
										.highest_education_level
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.public_information
									.professional_qualifications
									.highest_education_level ?? '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Học hàm
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode">
							<input
								type="text"
								placeholder="Ví dụ: Thạc sĩ / Giảng viên / Nghiên cứu viên"
								v-model="
									staffData.public_information
										.professional_qualifications
										.academic_rank
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.public_information
									.professional_qualifications
									.academic_rank ?? '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Học vị
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode">
							<input
								type="text"
								placeholder="Ví dụ: Cử nhân / Thạc sĩ / Tiến sĩ"
								v-model="
									staffData.public_information
										.professional_qualifications
										.academic_degree
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.public_information
									.professional_qualifications
									.academic_degree ?? '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Chức danh / Vị trí công việc chính thức
					</div>
					<div
						v-if="
							staffData.public_information
								.professional_qualifications.official_job_title
								.length > 0
						"
					>
						<div
							v-for="(m, i) in staffData.public_information
								.professional_qualifications.official_job_title"
							:key="i"
						>
							<div class="mt-1 input-group">
								<div v-if="editMode">
									<div class="flex items-center gap-2">
										<input
											type="text"
											placeholder="Ví dụ: {{ $filters.terminology('teacher') }} / Trưởng chuyên môn/ Quản lý"
											v-model="
												staffData.public_information
													.professional_qualifications
													.official_job_title[i]
											"
										/>
										<div
											v-if="
												staffData.public_information
													.professional_qualifications
													.official_job_title[i]
											"
											class="text-yellow-600 font-semibold text-xs cursor-pointer"
											@click="
												deleteInput(
													i,
													'official_job_title'
												)
											"
										>
											<img
												class="w-6 h-6 cursor-pointer"
												src="@/assets/images/icons/trash-fill-blue.png"
												alt=""
											/>
										</div>
									</div>
								</div>
								<div v-else>{{ m || '-' }}</div>
							</div>
						</div>
					</div>
					<div v-else>-</div>
					<div
						v-if="editMode"
						class="flex items-center justify-end w-full mt-2 cursor-pointer"
						@click="addInput('official_job_title')"
					>
						<img
							src="@/assets/images/icons/yellow-plus.png"
							alt=""
						/>
						<div
							class="ml-1 font-semibold text-yellow-700 text-sm'"
						>
							Thêm thông tin
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Kinh nghiệm làm việc (năm)
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode">
							<input
								type="text"
								placeholder="Ví dụ: 3 năm / 7 năm"
								v-model="
									staffData.public_information
										.professional_qualifications
										.years_of_experience
								"
							/>
						</div>
						<div v-else>
							{{
								staffData.public_information
									.professional_qualifications
									.years_of_experience ?? '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Đối tượng đã làm việc
					</div>

					<div
						v-if="
							staffData.public_information
								.professional_qualifications
								.target_group_experience.length > 0
						"
					>
						<div
							v-for="(m, i) in staffData.public_information
								.professional_qualifications
								.target_group_experience"
							:key="i"
						>
							<div class="mt-1 input-group">
								<div v-if="editMode">
									<div class="flex items-center gap-2">
										<input
											type="text"
											placeholder="Ví dụ: Dạng rối loạn / khó khăn và độ tuổi"
											v-model="
												staffData.public_information
													.professional_qualifications
													.target_group_experience[i]
											"
										/>
										<div
											v-if="
												staffData.public_information
													.professional_qualifications
													.target_group_experience[i]
											"
											class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
											@click="
												deleteInput(
													i,
													'target_group_experience'
												)
											"
										>
											<img
												src="@/assets/images/icons/trash-fill-blue.png"
												class="w-6 h-6 cursor-pointer"
												alt=""
											/>
										</div>
									</div>
								</div>
								<div v-else>{{ m || '-' }}</div>
							</div>
						</div>
					</div>
					<div v-else>-</div>
					<div
						v-if="editMode"
						class="flex items-center justify-end w-full mt-2 cursor-pointer"
						@click="addInput('target_group_experience')"
					>
						<img
							src="@/assets/images/icons/yellow-plus.png"
							alt=""
						/>
						<div
							class="ml-1 font-semibold text-yellow-700 text-sm'"
						>
							Thêm thông tin
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Thế mạnh, thành công nổi bật
					</div>
					<div
						v-if="
							staffData.public_information
								.professional_qualifications.key_achievements
								.length > 0
						"
					>
						<div
							v-for="(m, i) in staffData.public_information
								.professional_qualifications.key_achievements"
							:key="i"
						>
							<div class="mt-1 input-group">
								<div v-if="editMode">
									<div class="flex items-center gap-2">
										<input
											type="text"
											placeholder="Ví dụ: Kỹ năng can thiệp ngôn ngữ / Hỗ trợ cải thiện giao tiếp xã hội"
											v-model="
												staffData.public_information
													.professional_qualifications
													.key_achievements[i]
											"
										/>
										<div
											v-if="
												staffData.public_information
													.professional_qualifications
													.key_achievements[i]
											"
											class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
											@click="
												deleteInput(
													i,
													'key_achievements'
												)
											"
										>
											<img
												src="@/assets/images/icons/trash-fill-blue.png"
												class="w-6 h-6 cursor-pointer"
												alt=""
											/>
										</div>
									</div>
								</div>
								<div v-else>{{ m || '-' }}</div>
							</div>
						</div>
					</div>
					<div v-else>-</div>
					<div
						v-if="editMode"
						class="flex items-center justify-end w-full mt-2 cursor-pointer"
						@click="addInput('key_achievements')"
					>
						<img
							src="@/assets/images/icons/yellow-plus.png"
							alt=""
						/>
						<div
							class="ml-1 font-semibold text-yellow-700 text-sm'"
						>
							Thêm thông tin
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Khả năng ngoại ngữ
					</div>
					<div
						v-if="
							staffData.public_information
								.professional_qualifications.language_skills
								.length > 0
						"
					>
						<div
							v-for="(m, i) in staffData.public_information
								.professional_qualifications.language_skills"
							:key="i"
						>
							<div class="mt-1 input-group">
								<div v-if="editMode">
									<div class="flex items-center gap-2">
										<input
											type="text"
											placeholder="Ví dụ: Tiếng Anh giao tiếp / Đọc hiểu tài liệu chuyên ngành"
											v-model="
												staffData.public_information
													.professional_qualifications
													.language_skills[i]
											"
										/>
										<div
											v-if="
												staffData.public_information
													.professional_qualifications
													.language_skills[i]
											"
											class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
											@click="
												deleteInput(
													i,
													'language_skills'
												)
											"
										>
											<img
												src="@/assets/images/icons/trash-fill-blue.png"
												class="w-6 h-6 cursor-pointer"
												alt=""
											/>
										</div>
									</div>
								</div>
								<div v-else>{{ m || '-' }}</div>
							</div>
						</div>
					</div>
					<div v-else>-</div>
					<div
						v-if="editMode"
						class="flex items-center justify-end w-full mt-2 cursor-pointer"
						@click="addInput('language_skills')"
					>
						<img
							src="@/assets/images/icons/yellow-plus.png"
							alt=""
						/>
						<div
							class="ml-1 font-semibold text-yellow-700 text-sm'"
						>
							Thêm thông tin
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Sử dụng công nghệ, phần mềm
					</div>
					<div
						v-if="
							staffData.public_information
								.professional_qualifications.tech_skills
								.length > 0
						"
					>
						<div
							v-for="(m, i) in staffData.public_information
								.professional_qualifications.tech_skills"
							:key="i"
						>
							<div class="mt-1 input-group">
								<div v-if="editMode">
									<div class="flex items-center gap-2">
										<input
											type="text"
											placeholder="Ví dụ: Microsoft Office / Canva / phần mềm quản lý ca trị liệu"
											v-model="
												staffData.public_information
													.professional_qualifications
													.tech_skills[i]
											"
										/>
										<div
											v-if="
												staffData.public_information
													.professional_qualifications
													.tech_skills[i]
											"
											class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
											@click="
												deleteInput(i, 'tech_skills')
											"
										>
											<img
												src="@/assets/images/icons/trash-fill-blue.png"
												class="w-6 h-6 cursor-pointer"
												alt=""
											/>
										</div>
									</div>
								</div>
								<div v-else>{{ m || '-' }}</div>
							</div>
						</div>
					</div>
					<div v-else>-</div>
					<div
						v-if="editMode"
						class="flex items-center justify-end w-full mt-2 cursor-pointer"
						@click="addInput('tech_skills')"
					>
						<img
							src="@/assets/images/icons/yellow-plus.png"
							alt=""
						/>
						<div
							class="ml-1 font-semibold text-yellow-700 text-sm'"
						>
							Thêm thông tin
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Khóa học, hoạt động phát triển bản thân
					</div>
					<div
						v-if="
							staffData.public_information
								.professional_qualifications
								.development_activities.length > 0
						"
					>
						<div
							v-for="(m, i) in staffData.public_information
								.professional_qualifications
								.development_activities"
							:key="i"
						>
							<div class="mt-1 input-group">
								<div v-if="editMode">
									<div class="flex items-center gap-2">
										<input
											type="text"
											placeholder="Ví dụ: Workshop về kỹ năng trị liệu cảm xúc / Khóa đào tạo quản lý"
											v-model="
												staffData.public_information
													.professional_qualifications
													.development_activities[i]
											"
										/>
										<div
											v-if="
												staffData.public_information
													.professional_qualifications
													.development_activities[i]
											"
											class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
											@click="
												deleteInput(
													i,
													'development_activities'
												)
											"
										>
											<img
												src="@/assets/images/icons/trash-fill-blue.png"
												class="w-6 h-6 cursor-pointer"
												alt=""
											/>
										</div>
									</div>
								</div>
								<div v-else>{{ m || '-' }}</div>
							</div>
						</div>
					</div>
					<div v-else>-</div>
					<div
						v-if="editMode"
						class="flex items-center justify-end w-full mt-2 cursor-pointer"
						@click="addInput('development_activities')"
					>
						<img
							src="@/assets/images/icons/yellow-plus.png"
							alt=""
						/>
						<div
							class="ml-1 font-semibold text-yellow-700 text-sm'"
						>
							Thêm thông tin
						</div>
					</div>
				</div>
			</div>
			<div class="bg-white rounded-2xl shadow-sm w-full" id="3">
				<div class="flex items-center justify-between py-4 px-6 gap-2">
					<div class="font-semibold text-neutral-600">
						Phần 3: Bằng cấp, chứng chỉ và giải thưởng
					</div>
					<DaIcon
						v-if="!editMode"
						name="das--pen"
						class="h-5 w-5 cursor-pointer text-blue-600 hover:text-blue-900 active:text-blue-500"
						@click="editData('3')"
					/>
					<button
						v-else
						type="button"
						class="button-p text-center w-max"
						@click="updateProfile"
					>
						Lưu thay đổi
					</button>
				</div>
				<div class="py-4 px-6">
					<div
						v-if="
							staffData.public_information.certificates_and_awards
								.degrees.length > 0
						"
					>
						<div
							v-for="(degree, index) in staffData
								.public_information.certificates_and_awards
								.degrees"
							:key="degree"
						>
							<div class="flex items-center justify-between">
								<div class="text-neutral-800">
									Bằng cấp {{ index + 1 }}
								</div>
								<div
									v-if="
										editMode &&
										staffData.public_information
											.certificates_and_awards.degrees
											.length > 1
									"
									class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
									@click="deleteInput(index, 'degrees')"
								>
									<img
										class="w-6 h-6 cursor-pointer"
										src="@/assets/images/icons/trash-fill-blue.png"
										alt=""
									/>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Bằng cấp
								</div>
								<div class="mt-1 input-group">
									<div v-if="editMode" class="w-full">
										<input
											type="text"
											placeholder="Ví dụ: Thạc sĩ / Giảng viên / Nghiên cứu viên"
											v-model="degree.degree_name"
										/>
									</div>
									<div v-else>
										{{ degree.degree_name || '-' }}
									</div>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Trường
								</div>
								<div class="mt-1 input-group">
									<div v-if="editMode" class="w-full">
										<input
											type="text"
											placeholder="Ví dụ: Đại học Sư phạm"
											v-model="degree.institution_name"
										/>
									</div>
									<div v-else>
										{{ degree.institution_name || '-' }}
									</div>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Chuyên ngành
								</div>
								<div class="mt-1 input-group">
									<div v-if="editMode" class="w-full">
										<input
											type="text"
											placeholder="Ví dụ: Giáo dục mầm non"
											v-model="degree.degree_major"
										/>
									</div>
									<div v-else>
										{{ degree.degree_major || '-' }}
									</div>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Tải lên bằng cấp
								</div>
								<div class="mt-2">
									<div
										v-if="
											editMode && !degree.degree_file_url
										"
										class="w-full text-neutral-500 bg-white flex flex-col items-center justify-senter text-center gap-2 rounded-lg p-8 cursor-pointer border-2 border-dashed hover:border-yellow-500"
										@click="
											openModelUpload('degrees', index)
										"
									>
										<img
											src="@/assets/images/icons/cloud-upload-fill.png"
											alt=""
										/>
										<div class="font-semibold">
											Tải lên tệp hoặc ảnh đính kèm
										</div>
										<div class="text-sm">
											PNG, JPG & PDF
										</div>
									</div>
									<div v-else-if="degree.degree_file_url">
										<div
											v-if="
												degree.type !==
												'application/pdf'
											"
											class="rounded-lg relative"
										>
											<div
												class="absolute top-2 right-2 cursor-pointer"
												@click="
													removeImage(
														index,
														'degrees'
													)
												"
												v-if="editMode"
											>
												<svg
													width="24"
													height="24"
													viewBox="0 0 12 12"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<g opacity="0.5">
														<rect
															width="12"
															height="12"
															rx="6"
															fill="black"
														/>
													</g>
													<path
														fill-rule="evenodd"
														clip-rule="evenodd"
														d="M3.73483 3.73483C3.88128 3.58839 4.11872 3.58839 4.26517 3.73483L6.25 5.71967L8.23484 3.73483C8.38128 3.58839 8.61872 3.58839 8.76516 3.73483C8.91161 3.88128 8.91161 4.11872 8.76516 4.26517L6.78033 6.25L8.76516 8.23484C8.91161 8.38128 8.91161 8.61872 8.76516 8.76516C8.61872 8.91161 8.38128 8.91161 8.23484 8.76516L6.25 6.78033L4.26517 8.76516C4.11872 8.91161 3.88128 8.91161 3.73483 8.76516C3.58839 8.61872 3.58839 8.38128 3.73483 8.23484L5.71967 6.25L3.73483 4.26517C3.58839 4.11872 3.58839 3.88128 3.73483 3.73483Z"
														fill="white"
													/>
												</svg>
											</div>
											<img
												:src="degree?.degree_file_url"
												alt=""
												class="rounded-lg object-fill h-full w-full"
											/>
										</div>
										<div
											v-if="
												degree.type ===
												'application/pdf'
											"
											class="rounded-lg relative"
										>
											<div
												class="text-neutral-400 flex items-center gap-2 justify-between"
											>
												<div
													class="flex items-center gap-2"
												>
													<svg
														width="24"
														height="32"
														viewBox="0 0 24 32"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															fill-rule="evenodd"
															clip-rule="evenodd"
															d="M0 4C0 1.79086 1.79086 0 4 0H14.5858C15.1162 0 15.6249 0.210714 16 0.585786L23.4142 8C23.7893 8.37507 24 8.88378 24 9.41421V28C24 30.2091 22.2091 32 20 32H4C1.79086 32 0 30.2091 0 28V4ZM15 7V3L21 9H17C15.8954 9 15 8.10457 15 7ZM17.7071 15.7071C18.0976 15.3166 18.0976 14.6834 17.7071 14.2929C17.3166 13.9024 16.6834 13.9024 16.2929 14.2929L11 19.5858L8.70711 17.2929C8.31658 16.9024 7.68342 16.9024 7.29289 17.2929C6.90237 17.6834 6.90237 18.3166 7.29289 18.7071L10.2929 21.7071C10.4804 21.8946 10.7348 22 11 22C11.2652 22 11.5196 21.8946 11.7071 21.7071L17.7071 15.7071Z"
															fill="#E5A42B"
														/>
													</svg>
													<a
														:href="
															degree?.degree_file_url
														"
														target="_blank"
														class="hover:underline cursor-pointer"
													>
														{{
															degree.file_name
														}}</a
													>
												</div>
												<img
													@click.stop="
														removeImage(
															index,
															'degrees'
														)
													"
													src="@/assets/images/icons/trash-fill-blue.png"
													class="w-6 h-6 cursor-pointer"
													alt=""
												/>
											</div>
										</div>
									</div>
									<div v-else>-</div>
								</div>
							</div>
						</div>
						<div
							v-if="editMode"
							class="flex items-center justify-end w-full mt-2 cursor-pointer"
							@click="addInput('degrees')"
						>
							<img
								src="@/assets/images/icons/yellow-plus.png"
								alt=""
							/>
							<div
								class="ml-1 font-semibold text-yellow-700 text-sm pr-4"
							>
								Thêm thông tin
							</div>
						</div>
					</div>
					<div v-else>
						<div class="text-neutral-800">Bằng cấp</div>
						<div>-</div>
					</div>
				</div>
				<div style="border: 1px solid #faf5f1"></div>
				<div class="py-4 px-6">
					<div
						v-if="
							staffData.public_information.certificates_and_awards
								.certificates.length > 0
						"
					>
						<div
							v-for="(certificate, index) in staffData
								.public_information.certificates_and_awards
								.certificates"
							:key="certificate"
						>
							<div class="flex items-center justify-between">
								<div class="text-neutral-800">
									Chứng chỉ {{ index + 1 }}
								</div>
								<div
									v-if="
										editMode &&
										staffData.public_information
											.certificates_and_awards
											.certificates.length > 1
									"
									class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
									@click="deleteInput(index, 'certificates')"
								>
									<img
										class="w-6 h-6 cursor-pointer"
										src="@/assets/images/icons/trash-fill-blue.png"
										alt=""
									/>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Khóa học, chứng chỉ đã hoàn thành
								</div>
								<div class="mt-1 input-group">
									<div v-if="editMode" class="w-full">
										<input
											type="text"
											placeholder="Ví dụ: PECS / ABA cơ bản / DIR Floortime"
											v-model="certificate.cert_name"
										/>
									</div>
									<div v-else>
										{{ certificate.cert_name || '-' }}
									</div>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Mô tả
								</div>
								<div class="mt-1 input-group">
									<div v-if="editMode" class="w-full">
										<textarea
											type="text"
											placeholder="Ví dụ: Mô tả về Khóa học, chứng chỉ đã hoàn thành"
											v-model="
												certificate.cert_description
											"
										/>
									</div>
									<div v-else>
										{{
											certificate.cert_description || '-'
										}}
									</div>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Tải lên Chứng chỉ, chứng nhận
								</div>
								<div class="mt-2">
									<div
										v-if="
											editMode &&
											!certificate.cert_file_url
										"
										class="w-full text-neutral-500 bg-white flex flex-col items-center justify-senter text-center gap-2 rounded-lg p-8 cursor-pointer border-2 border-dashed hover:border-yellow-500"
										@click="
											openModelUpload(
												'certificates',
												index
											)
										"
									>
										<img
											src="@/assets/images/icons/cloud-upload-fill.png"
											alt=""
										/>
										<div class="font-semibold">
											Tải lên tệp hoặc ảnh đính kèm
										</div>
										<div class="text-sm">
											PNG, JPG & PDF
										</div>
									</div>
									<div v-else-if="certificate.cert_file_url">
										<div
											v-if="
												certificate.type !==
												'application/pdf'
											"
											class="rounded-lg relative"
										>
											<div
												class="absolute top-2 right-2 cursor-pointer"
												@click="
													removeImage(
														index,
														'certificates'
													)
												"
												v-if="editMode"
											>
												<svg
													width="24"
													height="24"
													viewBox="0 0 12 12"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<g opacity="0.5">
														<rect
															width="12"
															height="12"
															rx="6"
															fill="black"
														/>
													</g>
													<path
														fill-rule="evenodd"
														clip-rule="evenodd"
														d="M3.73483 3.73483C3.88128 3.58839 4.11872 3.58839 4.26517 3.73483L6.25 5.71967L8.23484 3.73483C8.38128 3.58839 8.61872 3.58839 8.76516 3.73483C8.91161 3.88128 8.91161 4.11872 8.76516 4.26517L6.78033 6.25L8.76516 8.23484C8.91161 8.38128 8.91161 8.61872 8.76516 8.76516C8.61872 8.91161 8.38128 8.91161 8.23484 8.76516L6.25 6.78033L4.26517 8.76516C4.11872 8.91161 3.88128 8.91161 3.73483 8.76516C3.58839 8.61872 3.58839 8.38128 3.73483 8.23484L5.71967 6.25L3.73483 4.26517C3.58839 4.11872 3.58839 3.88128 3.73483 3.73483Z"
														fill="white"
													/>
												</svg>
											</div>
											<img
												:src="certificate.cert_file_url"
												alt=""
												class="rounded-lg object-fill h-full w-full"
											/>
										</div>
										<div
											v-if="
												certificate.type ===
												'application/pdf'
											"
											class="rounded-lg relative"
										>
											<div
												class="text-neutral-400 flex items-center gap-2 justify-between"
											>
												<div
													class="flex items-center gap-2"
												>
													<svg
														width="24"
														height="32"
														viewBox="0 0 24 32"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															fill-rule="evenodd"
															clip-rule="evenodd"
															d="M0 4C0 1.79086 1.79086 0 4 0H14.5858C15.1162 0 15.6249 0.210714 16 0.585786L23.4142 8C23.7893 8.37507 24 8.88378 24 9.41421V28C24 30.2091 22.2091 32 20 32H4C1.79086 32 0 30.2091 0 28V4ZM15 7V3L21 9H17C15.8954 9 15 8.10457 15 7ZM17.7071 15.7071C18.0976 15.3166 18.0976 14.6834 17.7071 14.2929C17.3166 13.9024 16.6834 13.9024 16.2929 14.2929L11 19.5858L8.70711 17.2929C8.31658 16.9024 7.68342 16.9024 7.29289 17.2929C6.90237 17.6834 6.90237 18.3166 7.29289 18.7071L10.2929 21.7071C10.4804 21.8946 10.7348 22 11 22C11.2652 22 11.5196 21.8946 11.7071 21.7071L17.7071 15.7071Z"
															fill="#E5A42B"
														/>
													</svg>
													<a
														:href="
															certificate.cert_file_url
														"
														target="_blank"
														class="hover:underline cursor-pointer"
													>
														{{
															certificate.file_name
														}}</a
													>
												</div>
												<img
													@click.stop="
														removeImage(
															index,
															'certificates'
														)
													"
													src="@/assets/images/icons/trash-fill-blue.png"
													class="w-6 h-6 cursor-pointer"
													alt=""
												/>
											</div>
										</div>
									</div>
									<div v-else>-</div>
								</div>
							</div>
						</div>
						<div
							v-if="editMode"
							class="flex items-center justify-end w-full mt-2 cursor-pointer"
							@click="addInput('certificates')"
						>
							<img
								src="@/assets/images/icons/yellow-plus.png"
								alt=""
							/>
							<div
								class="ml-1 font-semibold text-yellow-700 text-sm pr-4"
							>
								Thêm thông tin
							</div>
						</div>
					</div>
					<div v-else>
						<div class="text-neutral-800">Chứng chỉ</div>
						<div>-</div>
					</div>
				</div>
				<div style="border: 1px solid #faf5f1"></div>
				<div class="py-4 px-6">
					<div
						v-if="
							staffData.public_information.certificates_and_awards
								.awards.length > 0
						"
					>
						<div
							v-for="(award, index) in staffData
								.public_information.certificates_and_awards
								.awards"
							:key="award"
						>
							<div class="flex items-center justify-between">
								<div class="text-neutral-800">
									Giải thưởng, giấy khen {{ index + 1 }}
								</div>
								<div
									v-if="
										editMode &&
										staffData.public_information
											.certificates_and_awards.awards
											.length > 1
									"
									class="text-yellow-600 font-semibold text-xs flex items-center gap-1 cursor-pointer"
									@click="deleteInput(index, 'awards')"
								>
									<img
										class="w-6 h-6 cursor-pointer"
										src="@/assets/images/icons/trash-fill-blue.png"
										alt=""
									/>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Giải thưởng, giấy khen
								</div>
								<div class="mt-1 input-group">
									<div v-if="editMode" class="w-full">
										<input
											type="text"
											placeholder="Ví dụ: Nhân viên xuất sắc năm 2023 / Giấy khen từ Trung tâm ABC"
											v-model="award.award_name"
										/>
									</div>
									<div v-else>
										{{ award.award_name || '-' }}
									</div>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Mô tả
								</div>
								<div class="mt-1 input-group">
									<div v-if="editMode" class="w-full">
										<textarea
											type="text"
											placeholder="Ví dụ: Mô tả về Giải thưởng, giấy khen"
											v-model="award.award_description"
										/>
									</div>
									<div v-else>
										{{ award.award_description || '-' }}
									</div>
								</div>
							</div>
							<div class="p-4">
								<div
									class="uppercase text-neutral-400 font-semibold text-xs"
								>
									Tải lên Giải thưởng, giấy khen
								</div>
								<div class="mt-2">
									<div
										v-if="editMode && !award.award_file_url"
										class="w-full text-neutral-500 bg-white flex flex-col items-center justify-senter text-center gap-2 rounded-lg p-8 cursor-pointer border-2 border-dashed hover:border-yellow-500"
										@click="
											openModelUpload('awards', index)
										"
									>
										<img
											src="@/assets/images/icons/cloud-upload-fill.png"
											alt=""
										/>
										<div class="font-semibold">
											Tải lên tệp hoặc ảnh đính kèm
										</div>
										<div class="text-sm">
											PNG, JPG & PDF
										</div>
									</div>
									<div v-else-if="award.award_file_url">
										<div
											v-if="
												award.type !== 'application/pdf'
											"
											class="rounded-lg relative"
										>
											<div
												class="absolute top-2 right-2 cursor-pointer"
												@click="
													removeImage(index, 'awards')
												"
												v-if="editMode"
											>
												<svg
													width="24"
													height="24"
													viewBox="0 0 12 12"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<g opacity="0.5">
														<rect
															width="12"
															height="12"
															rx="6"
															fill="black"
														/>
													</g>
													<path
														fill-rule="evenodd"
														clip-rule="evenodd"
														d="M3.73483 3.73483C3.88128 3.58839 4.11872 3.58839 4.26517 3.73483L6.25 5.71967L8.23484 3.73483C8.38128 3.58839 8.61872 3.58839 8.76516 3.73483C8.91161 3.88128 8.91161 4.11872 8.76516 4.26517L6.78033 6.25L8.76516 8.23484C8.91161 8.38128 8.91161 8.61872 8.76516 8.76516C8.61872 8.91161 8.38128 8.91161 8.23484 8.76516L6.25 6.78033L4.26517 8.76516C4.11872 8.91161 3.88128 8.91161 3.73483 8.76516C3.58839 8.61872 3.58839 8.38128 3.73483 8.23484L5.71967 6.25L3.73483 4.26517C3.58839 4.11872 3.58839 3.88128 3.73483 3.73483Z"
														fill="white"
													/>
												</svg>
											</div>
											<img
												:src="award.award_file_url"
												alt=""
												class="rounded-lg object-fill h-full w-full"
											/>
										</div>
										<div
											v-if="
												award.type === 'application/pdf'
											"
											class="rounded-lg relative"
										>
											<div
												class="text-neutral-400 flex items-center gap-2 justify-between"
											>
												<div
													class="flex items-center gap-2"
												>
													<svg
														width="24"
														height="32"
														viewBox="0 0 24 32"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															fill-rule="evenodd"
															clip-rule="evenodd"
															d="M0 4C0 1.79086 1.79086 0 4 0H14.5858C15.1162 0 15.6249 0.210714 16 0.585786L23.4142 8C23.7893 8.37507 24 8.88378 24 9.41421V28C24 30.2091 22.2091 32 20 32H4C1.79086 32 0 30.2091 0 28V4ZM15 7V3L21 9H17C15.8954 9 15 8.10457 15 7ZM17.7071 15.7071C18.0976 15.3166 18.0976 14.6834 17.7071 14.2929C17.3166 13.9024 16.6834 13.9024 16.2929 14.2929L11 19.5858L8.70711 17.2929C8.31658 16.9024 7.68342 16.9024 7.29289 17.2929C6.90237 17.6834 6.90237 18.3166 7.29289 18.7071L10.2929 21.7071C10.4804 21.8946 10.7348 22 11 22C11.2652 22 11.5196 21.8946 11.7071 21.7071L17.7071 15.7071Z"
															fill="#E5A42B"
														/>
													</svg>
													<a
														:href="
															award.award_file_url
														"
														target="_blank"
														class="hover:underline cursor-pointer"
													>
														{{ award.file_name }}</a
													>
												</div>
												<img
													@click.stop="
														removeImage(
															index,
															'awards'
														)
													"
													src="@/assets/images/icons/trash-fill-blue.png"
													class="w-6 h-6 cursor-pointer"
													alt=""
												/>
											</div>
										</div>
									</div>
									<div v-else>-</div>
								</div>
							</div>
						</div>
						<div
							v-if="editMode"
							class="flex items-center justify-end w-full mt-2 cursor-pointer"
							@click="addInput('awards')"
						>
							<img
								src="@/assets/images/icons/yellow-plus.png"
								alt=""
							/>
							<div
								class="ml-1 font-semibold text-yellow-700 text-sm pr-4"
							>
								Thêm thông tin
							</div>
						</div>
					</div>
					<div v-else>
						<div class="text-neutral-800">
							Giải thưởng, giấy khen
						</div>
						<div>-</div>
					</div>
				</div>
			</div>
			<div class="bg-white rounded-2xl shadow-sm w-full px-6" id="5">
				<div class="flex items-center justify-between py-4 gap-2">
					<div class="font-semibold text-neutral-600">
						Phần 5: Định hướng và phong cách làm việc
					</div>
					<DaIcon
						v-if="!editMode"
						name="das--pen"
						class="h-5 w-5 cursor-pointer text-blue-600 hover:text-blue-900 active:text-blue-500"
						@click="editData('5')"
					/>
					<button
						v-else
						type="button"
						class="button-p text-center w-max"
						@click="updateProfile"
					>
						Lưu thay đổi
					</button>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Quan điểm cá nhân về việc can thiệp
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<textarea
								type="text"
								placeholder="Ví dụ: Lựa chọn tiếp cận, ứng dụng phương pháp, sử dụng chương trình, cách kết nối với trẻ..."
								v-model="
									staffData.public_information
										.orientation_and_work_style
										.intervention_philosophy
								"
							/>
						</div>
						<div v-else>
							{{
								staffData?.public_information
									?.orientation_and_work_style
									?.intervention_philosophy ?? '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Cam kết, nguyên tắc đồng hành cùng phụ huynh
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<textarea
								type="text"
								placeholder="Ví dụ: Chia sẻ cam kết và cách bạn đồng hành cùng phụ huynh"
								v-model="
									staffData.public_information
										.orientation_and_work_style
										.parent_commitment_principles
								"
							/>
						</div>
						<div v-else>
							{{
								staffData?.public_information
									?.orientation_and_work_style
									?.parent_commitment_principles ?? '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Khả năng phối hợp với đồng nghiệp
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<textarea
								type="text"
								placeholder="Ví dụ: Mô tả cách bạn hợp tác và làm việc nhóm"
								v-model="
									staffData.public_information
										.orientation_and_work_style
										.collaboration_skills
								"
							/>
						</div>
						<div v-else>
							{{
								staffData?.public_information
									?.orientation_and_work_style
									?.collaboration_skills ?? '-'
							}}
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Slogan của bạn là
					</div>
					<div class="mt-1 input-group">
						<div v-if="editMode" class="w-full">
							<textarea
								type="text"
								placeholder="Ví dụ: Nhập câu slogan thể hiện giá trị nghề nghiệp của bạn"
								v-model="
									staffData.public_information
										.orientation_and_work_style
										.personal_slogan
								"
							/>
						</div>
						<div v-else>
							{{
								staffData?.public_information
									?.orientation_and_work_style
									?.personal_slogan ?? '-'
							}}
						</div>
					</div>
				</div>
			</div>
			<div class="bg-white rounded-2xl shadow-sm w-full px-6" id="6">
				<div class="flex items-center justify-between py-4 gap-2">
					<div class="font-semibold text-neutral-600">
						Phần 6: Tài liệu đính kèm
					</div>
					<DaIcon
						v-if="!editMode"
						name="das--pen"
						class="h-5 w-5 cursor-pointer text-blue-600 hover:text-blue-900 active:text-blue-500"
						@click="editData('6')"
					/>
					<button
						v-else
						type="button"
						class="button-p text-center w-max"
						@click="updateProfile"
					>
						Lưu thay đổi
					</button>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						Ảnh đại diện
					</div>
					<div class="mt-1 input-group">
						<div class="flex items-end justify-between">
							<img
								class="shadow-sm inline-block h-14 w-14 rounded-full"
								:src="
									staffData?.public_information?.attachments
										?.avatar_url ||
									staffData.staff?.profile_photo?.w200
								"
								alt=""
								v-if="
									staffData?.public_information?.attachments
										?.avatar_url ||
									staffData.staff?.profile_photo?.w200
								"
							/>
							<span
								v-else
								class="shadow-sm inline-flex items-center justify-center h-14 w-14 rounded-full text-white text-4xl"
								:style="{
									background:
										staffData.staff.profile_photo?.default
											?.color,
								}"
							>
								<span
									class="font-medium leading-none text-white"
									>{{
										staffData.staff.profile_photo?.default
											?.initial
									}}</span
								>
							</span>
							<div
								v-if="editMode"
								class="text-yellow-700 font-semibold text-sm cursor-pointer flex items-center gap-1"
								@click="openModelUpload('avatar')"
							>
								<img
									class="w-4 h-4"
									src="@/assets/images/icons/cloud-upload-fill.png"
									alt=""
								/>
								<div>Tải lên ảnh đại diện mới</div>
							</div>
						</div>
					</div>
				</div>
				<div class="py-4">
					<div
						class="uppercase text-neutral-400 font-semibold text-xs"
					>
						CV/ sơ yếu lý lịch
					</div>
					<div class="mt-2">
						<div
							v-if="editMode && !attachments?.resume_file_name"
							class="w-full text-neutral-500 bg-white flex flex-col items-center justify-senter text-center gap-2 rounded-lg p-8 cursor-pointer border-2 border-dashed hover:border-yellow-500"
							@click="openModelUpload('resume')"
						>
							<img
								src="@/assets/images/icons/cloud-upload-fill.png"
								alt=""
							/>
							<div class="font-semibold">
								Tải lên tệp hoặc ảnh đính kèm
							</div>
							<div class="text-sm">PNG, JPG & PDF</div>
						</div>
						<div v-else-if="attachments?.resume_file_name">
							<div
								v-if="
									attachments.resume_type !==
									'application/pdf'
								"
								class="rounded-lg relative"
							>
								<div
									class="absolute top-2 right-2 cursor-pointer"
									@click="removeImage(0, 'resume')"
									v-if="editMode"
								>
									<svg
										width="24"
										height="24"
										viewBox="0 0 12 12"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g opacity="0.5">
											<rect
												width="12"
												height="12"
												rx="6"
												fill="black"
											/>
										</g>
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M3.73483 3.73483C3.88128 3.58839 4.11872 3.58839 4.26517 3.73483L6.25 5.71967L8.23484 3.73483C8.38128 3.58839 8.61872 3.58839 8.76516 3.73483C8.91161 3.88128 8.91161 4.11872 8.76516 4.26517L6.78033 6.25L8.76516 8.23484C8.91161 8.38128 8.91161 8.61872 8.76516 8.76516C8.61872 8.91161 8.38128 8.91161 8.23484 8.76516L6.25 6.78033L4.26517 8.76516C4.11872 8.91161 3.88128 8.91161 3.73483 8.76516C3.58839 8.61872 3.58839 8.38128 3.73483 8.23484L5.71967 6.25L3.73483 4.26517C3.58839 4.11872 3.58839 3.88128 3.73483 3.73483Z"
											fill="white"
										/>
									</svg>
								</div>
								<img
									:src="attachments.resume_url"
									alt=""
									class="rounded-lg object-fill h-full w-full"
								/>
							</div>
							<div
								v-if="
									attachments.resume_type ===
									'application/pdf'
								"
								class="rounded-lg relative"
							>
								<div
									class="text-neutral-400 flex items-center gap-2 justify-between"
								>
									<div class="flex items-center gap-2">
										<svg
											width="24"
											height="32"
											viewBox="0 0 24 32"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												fill-rule="evenodd"
												clip-rule="evenodd"
												d="M0 4C0 1.79086 1.79086 0 4 0H14.5858C15.1162 0 15.6249 0.210714 16 0.585786L23.4142 8C23.7893 8.37507 24 8.88378 24 9.41421V28C24 30.2091 22.2091 32 20 32H4C1.79086 32 0 30.2091 0 28V4ZM15 7V3L21 9H17C15.8954 9 15 8.10457 15 7ZM17.7071 15.7071C18.0976 15.3166 18.0976 14.6834 17.7071 14.2929C17.3166 13.9024 16.6834 13.9024 16.2929 14.2929L11 19.5858L8.70711 17.2929C8.31658 16.9024 7.68342 16.9024 7.29289 17.2929C6.90237 17.6834 6.90237 18.3166 7.29289 18.7071L10.2929 21.7071C10.4804 21.8946 10.7348 22 11 22C11.2652 22 11.5196 21.8946 11.7071 21.7071L17.7071 15.7071Z"
												fill="#E5A42B"
											/>
										</svg>
										<a
											:href="attachments.resume_url"
											target="_blank"
											class="hover:underline cursor-pointer"
										>
											{{
												attachments?.resume_file_name
											}}</a
										>
									</div>
									<img
										@click.stop="removeImage(0, 'resume')"
										src="@/assets/images/icons/trash-fill-blue.png"
										class="w-6 h-6 cursor-pointer"
										alt=""
									/>
								</div>
							</div>
						</div>
						<div v-else>-</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<style scoped>
.block-width {
	width: calc(50% - 16px);
}
.private-bg {
	background: #f1f5f9;
}
</style>
